import { makeAutoObservable, runInAction } from "mobx";
import { toast } from "react-toastify";
import { userService } from "services/user.service";
import authStore from "stores/auth/AuthStore";
import { User } from "types/user.type";

class UserStore {
  users: User[] = [];
  loading: boolean = false;
  selectedUser: User | null = null;
  filterUsers: { id: number; fullName: string }[] = [];
  actionLoading = new Map<number | "new", boolean>();
  formLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  setSelected(user: User | null) {
    this.selectedUser = user;
  }

  resetSelectedUser() {
    this.selectedUser = null;
  }

  setActionLoading(id: number | "new", value: boolean) {
    this.actionLoading.set(id, value);
  }

  isActionLoading(id: number | "new") {
    return this.actionLoading.get(id) ?? false;
  }

  setFormLoading(value: boolean) {
    this.formLoading = value;
  }

  isFormLoading() {
    return this.formLoading;
  }

  async fetchUsers() {
    if (!authStore.isAuthenticated()) return;

    this.loading = true;
    try {
      const data = await userService.getAll();
      runInAction(() => {
        this.users = data;
      });
    } catch (error: any) {
      if (
        error?.response?.data?.type === "role_changed" ||
        error?.response?.data?.type === "account_inactive"
      ) {
        return;
      }
      runInAction(() => {
        this.users = [];
      });
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async fetchFilterUsers() {
    if (!authStore.isAuthenticated()) return;

    this.loading = true;
    try {
      const data = await userService.getFilterUsers();
      runInAction(() => {
        this.filterUsers = data;
      });
    } catch (error) {
      runInAction(() => {
        this.filterUsers = [];
      });
      toast.error("Erreur lors du chargement des utilisateurs pour le filtre");
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async saveUser(user: Partial<User>) {
    const isNew = !user.id;

    this.setFormLoading(true);

    if (!isNew) {
      this.setActionLoading(user.id!, true);
    } else {
      this.setActionLoading("new", true);
    }

    try {
      if (user.id) {
        const updatedUser = await userService.update(user.id, user);
        runInAction(() => {
          const index = this.users.findIndex((u) => u.id === user.id);
          if (index !== -1) {
            this.users[index] = { ...this.users[index], ...updatedUser };
          }
        });
        toast.success("Utilisateur mis à jour !");
      } else {
        const newUser = await userService.create(user);
        runInAction(() => {
          this.users.push(newUser);
        });
        toast.success("Utilisateur créé !");
      }

      this.setSelected(null);
    } catch (error) {
      //   toast.error("Erreur lors de l'enregistrement");
      throw error;
    } finally {
      runInAction(() => {
        this.setFormLoading(false);
        if (!isNew) {
          this.setActionLoading(user.id!, false);
        } else {
          this.setActionLoading("new", false);
        }
      });
    }
  }

  async deleteUser(id: number) {
    this.setActionLoading(id, true);
    try {
      await userService.delete(id);
      runInAction(() => {
        this.users = this.users.filter((u) => u.id !== id);
      });
      toast.success("Utilisateur supprimé !");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      runInAction(() => {
        this.setActionLoading(id, false);
      });
    }
  }

  async updateUserStatus(id: number, isActive: boolean) {
    this.setActionLoading(id, true);
    try {
      await userService.update(id, { isActive });
      runInAction(() => {
        const index = this.users.findIndex((u) => u.id === id);
        if (index !== -1) {
          this.users[index].isActive = isActive;
        }
      });

      toast.success(`Utilisateur ${isActive ? "activé" : "désactivé"} !`);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
      throw error;
    } finally {
      runInAction(() => {
        this.setActionLoading(id, false);
      });
    }
  }
}

const userStore = new UserStore();
export default userStore;
